import { useTranslation } from 'react-i18next';
import { Input, Select } from '~/components/ui';
import { CurrencySelector } from '~/components/ui/inputs/CurrencySelector';
import type { Currency } from '~/graphql/graphql';
import { sellerLabel } from '~/utils';
import { shipFromSelectOptions } from '~/utils/facilityOptions';
import { NoShipFromFacilityNotice } from '~/pages/quote-response/NoShipFromFacilityNotice';
import { MAX_PRICE, MIN_POSITIVE_PRICE } from '~/utils/moneyBounds';
import { shipFromFacilitiesOf } from '~/pages/rfq-view/sellerResponseSeed';
import type { RowDraft } from '~/pages/quote-response/useQuoteResponseDraft';
import type { SellerResponse } from './useSellerResponseDrafts';
import type { Quote } from './splitAwardGridTypes';

export type SellerResponseField = keyof Pick<
  RowDraft,
  'quantityInBundles' | 'pickupPrice' | 'deliveryPrice'
>;

const PRICE_INPUT = {
  min: MIN_POSITIVE_PRICE,
  max: MAX_PRICE,
  step: '0.01',
} as const;

const INPUT_BOUNDS: Record<
  SellerResponseField,
  { min: number; max?: number; step: string }
> = {
  quantityInBundles: { min: 0, step: '1' },
  pickupPrice: PRICE_INPUT,
  deliveryPrice: PRICE_INPUT,
};

export function SellerResponseTerms({
  quote,
  response,
}: Readonly<{ quote: Quote; response: SellerResponse }>) {
  const { t } = useTranslation('rfq-view');
  const { draft, isSubmitting } = response;
  const facilities = shipFromFacilitiesOf(quote);

  return (
    <div
      role="group"
      aria-label={t('rfq_view.seller_response_terms_aria_label', {
        sellerName: sellerLabel(quote.sellingOrganization),
      })}
      className="mt-2 flex flex-col gap-2"
    >
      {facilities.length === 0 ? (
        <NoShipFromFacilityNotice
          linkedOrganizationId={quote.sellingOrganization.id}
        />
      ) : (
        <Select
          name={`${quote.id}-ship-from`}
          label={t('rfq_view.ship_from_label')}
          value={draft.sellingOrganizationFacilityId}
          disabled={isSubmitting || facilities.length === 1}
          onChange={(_, value) =>
            response.patch({ sellingOrganizationFacilityId: value })
          }
          options={shipFromSelectOptions(
            facilities,
            t('rfq_view.ship_from_placeholder')
          )}
        />
      )}
      <div className="grid grid-cols-2 gap-2">
        <CurrencySelector
          name={`${quote.id}-currency`}
          label={t('rfq_view.currency_label')}
          value={draft.currency}
          disabled={isSubmitting}
          onChange={(_, value) =>
            response.patch({ currency: value as Currency })
          }
        />
        <Input
          name={`${quote.id}-lead-time`}
          type="number"
          min={1}
          step="1"
          label={t('rfq_view.lead_time_label')}
          value={draft.leadTimeDays}
          disabled={isSubmitting}
          onChange={(_, value) => response.patch({ leadTimeDays: value })}
        />
      </div>
    </div>
  );
}

function missingValueErrorKey(
  response: SellerResponse,
  platformProductLengthId: string,
  field: SellerResponseField
) {
  if (
    field === 'quantityInBundles' &&
    response.rowIdsMissingQuantity.has(platformProductLengthId)
  ) {
    return 'rfq_view.quantity_required_error';
  }
  // The message names both prices, so one input per length carries it.
  if (
    field === 'pickupPrice' &&
    response.rowIdsMissingPrice.has(platformProductLengthId)
  ) {
    return 'rfq_view.price_required_error';
  }
  return undefined;
}

export function SellerResponseInput({
  quote,
  platformProductLengthId,
  field,
  ariaLabel,
  response,
}: Readonly<{
  quote: Quote;
  platformProductLengthId: string;
  field: SellerResponseField;
  ariaLabel: string;
  response: SellerResponse;
}>) {
  const { t } = useTranslation('rfq-view');
  const row = response.draft.rows.get(platformProductLengthId);
  if (!row) return '—';
  const { min, max, step } = INPUT_BOUNDS[field];
  const errorKey = missingValueErrorKey(
    response,
    platformProductLengthId,
    field
  );
  return (
    <Input
      name={`${quote.id}-${platformProductLengthId}-${field}`}
      aria-label={ariaLabel}
      type="number"
      min={min}
      max={max}
      step={step}
      containerClassName="max-w-[8rem]"
      value={row[field]}
      error={errorKey && t(errorKey)}
      disabled={response.isSubmitting}
      onChange={(_, value) =>
        response.patchRow(platformProductLengthId, { [field]: value })
      }
    />
  );
}
